'use client';

import { posthogIdentify, posthogSetBrandGroup } from '@lib/analytics/posthog';
import { useEffect, useRef } from 'react';

type Props = {
  /** Medusa customer ID (when authenticated). */
  customerId?: string | null;
  /** Customer email for person properties. */
  customerEmail?: string | null;
  /** Customer name for person properties. */
  customerName?: string | null;
  /** Brand handle, e.g. "myp" or "c4u". */
  brandHandle: string;
  /** Human-readable brand name, e.g. "MyPharmacy". */
  brandName: string;
};

/**
 * Client component that syncs PostHog identity and brand group.
 *
 * Rendered in the root layout to set the brand group on every page load,
 * and in the account layout with customer data to identify authenticated users.
 *
 * Uses refs to avoid re-identifying on every render — only fires when the
 * customer ID or brand handle actually changes.
 */
export default function PostHogIdentity({
  customerId,
  customerEmail,
  customerName,
  brandHandle,
  brandName,
}: Props) {
  const lastCustomerId = useRef<string | null>(null);
  const lastBrandHandle = useRef<string | null>(null);

  useEffect(() => {
    // Set brand group (only when handle changes)
    if (brandHandle && brandHandle !== lastBrandHandle.current) {
      posthogSetBrandGroup(brandHandle, brandName);
      lastBrandHandle.current = brandHandle;
    }

    // Identify customer (only when ID changes)
    if (customerId && customerId !== lastCustomerId.current) {
      const traits: Record<string, string> = {};
      if (customerEmail) traits.email = customerEmail;
      if (customerName) traits.name = customerName;
      posthogIdentify(customerId, traits);
      lastCustomerId.current = customerId;
    }
  }, [customerId, customerEmail, customerName, brandHandle, brandName]);

  return null;
}
